import { useInfiniteQuery, UseInfiniteQueryResult } from "react-query";
import { PAGE_SIZE } from "consts";
import { useHttpClient } from "contexts/httpClient";
import { AxiosInstance, AxiosResponse } from "axios";
import { useEffect } from "react";

interface FetchDataParams<T> {
  httpClient: AxiosInstance;
  pageParam: string;
  dataKey: string;
  apiUrl: string;
  dataInterceptor?: (data: T) => T;
  queryParams?: Record<string, any>;
}

interface FetchDataResult<T> {
  data: T;
  pageParam: string;
}

const fetchData = async <T>({
  httpClient,
  pageParam,
  dataKey,
  apiUrl,
  dataInterceptor,
  queryParams = {},
}: FetchDataParams<T>): Promise<FetchDataResult<T>> => {
  const response: AxiosResponse["data"] = await httpClient.get(apiUrl, {
    params: pageParam ? { pageToken: pageParam } : { pageSize: PAGE_SIZE, ...queryParams },
  });

  return {
    data: dataInterceptor
      ? response[dataKey].map(dataInterceptor)
      : response[dataKey],
    pageParam: response.nextPageToken,
  };
};

interface UseAutoFetchInfiniteQueryParams<T> {
  dataKey: string;
  apiUrl: string;
  queryKeys: string[];
  dataInterceptor?: (data: T) => T;
  queryParams?: Record<string, any>;
}

export function useAutoFetchInfiniteQuery<T>({
  dataKey,
  apiUrl,
  queryKeys,
  dataInterceptor,
  queryParams = {},
}: UseAutoFetchInfiniteQueryParams<T>): UseInfiniteQueryResult<T[], unknown> {
  const httpClient = useHttpClient();
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery<
    FetchDataResult<T>,
    unknown
  >(
    queryKeys,
    ({ pageParam = "" }) =>
      fetchData<T>({ httpClient, pageParam, dataKey, apiUrl, dataInterceptor, queryParams }),
    {
      getNextPageParam: ({ pageParam }) => pageParam || undefined,
      // @ts-expect-error - data is not defined in the type
      select: (data) => data?.pages?.flatMap((page) => page.data) || [],
      meta: {
        disableFullPageLoader: true,
      },
      staleTime: 1000 * 60 * 5,
    }
  );

  useEffect(() => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  return { data, isFetching, hasNextPage } as UseInfiniteQueryResult<
    T[],
    unknown
  >;
}
